/**
 * Full-screen mandatory MFA enrollment wizard.
 *
 * Rendered INSTEAD of the app whenever the backend answers a login or
 * registration with `mfa_stage: 'enrollment_required'`. The user cannot
 * reach any application surface until:
 *   1. they scan the QR (or enter the secret manually),
 *   2. verify a first TOTP against the pending secret,
 *   3. see their one-time recovery codes, and
 *   4. explicitly acknowledge they saved them.
 * Only the acknowledgment call mints the full session.
 */
import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ShieldCheck, Copy, DownloadSimple, ArrowRight } from '@phosphor-icons/react';

const STEP_SCAN = 'scan';
const STEP_CODES = 'codes';

export const MfaEnrollmentWizard = ({ challengeToken, email, onComplete, onCancel }) => {
  const { mfaSetup, mfaConfirm, mfaAcknowledge } = useAuth();
  const [step, setStep] = useState(STEP_SCAN);
  const [setupData, setSetupData] = useState(null);
  const [otp, setOtp] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);

  const expire = useCallback((err) => {
    if (err?.response?.status === 401) {
      toast.error('Your setup session expired. Please sign in again.');
      onCancel();
      return true;
    }
    return false;
  }, [onCancel]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await mfaSetup(challengeToken);
        if (active) setSetupData(data);
      } catch (err) {
        if (!expire(err)) toast.error(err.response?.data?.detail || 'Could not start MFA setup');
      }
    })();
    return () => { active = false; };
  }, [challengeToken, mfaSetup, expire]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await mfaConfirm(challengeToken, otp.trim());
      setRecoveryCodes(data.recovery_codes || []);
      setStep(STEP_CODES);
    } catch (err) {
      if (!expire(err)) toast.error(err.response?.data?.detail || 'Code did not verify — try the next code from your app');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setLoading(true);
    try {
      const user = await mfaAcknowledge(challengeToken);
      toast.success('Multi-factor authentication is active. Welcome!');
      onComplete(user);
    } catch (err) {
      if (!expire(err)) toast.error(err.response?.data?.detail || 'Could not finish setup');
    } finally {
      setLoading(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      toast.success('Recovery codes copied');
    } catch {
      toast.error('Clipboard copy failed — select and copy manually');
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [`AZAI Billing — MFA recovery codes for ${email || 'your account'}\nEach code works exactly once. Store securely.\n\n${recoveryCodes.join('\n')}\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'azai-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="mfa-enrollment-wizard">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-900 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">AZ</span>
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Secure your account</p>
            <p className="text-xs text-slate-500">
              Multi-factor authentication is required for every AZAI Billing user (HIPAA safeguard).
            </p>
          </div>
        </div>

        {step === STEP_SCAN && (
          <Card data-testid="mfa-wizard-scan-step">
            <CardHeader>
              <CardTitle>Step 1 of 2 — Set up your authenticator</CardTitle>
              <CardDescription>
                Scan the QR code with any authenticator app (Google Authenticator, Microsoft
                Authenticator, Authy, 1Password), then enter the 6-digit code it shows.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {!setupData ? (
                <div className="flex items-center justify-center h-48">
                  <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                    <div className="p-4 bg-white border border-slate-200 rounded-lg" data-testid="mfa-wizard-qr">
                      <QRCode value={setupData.provisioning_uri} size={180} />
                    </div>
                    <div className="flex-1 space-y-2 text-sm">
                      <Label className="text-xs text-slate-500">Can&apos;t scan? Enter this key manually</Label>
                      <code className="block bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs font-mono break-all" data-testid="mfa-wizard-secret">
                        {setupData.secret}
                      </code>
                      <p className="text-xs text-slate-500">
                        Issuer: <strong>AZAI Billing</strong> · {setupData.algorithm}
                      </p>
                    </div>
                  </div>
                  <form onSubmit={handleConfirm} className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="wizard-otp" className="text-slate-700">Enter the code from your app</Label>
                      <Input
                        id="wizard-otp"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        placeholder="123456"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                        className="font-mono tracking-widest text-center text-lg max-w-[180px]"
                        required
                        autoFocus
                        data-testid="mfa-wizard-otp-input"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <Button type="submit" disabled={loading || otp.length !== 6} data-testid="mfa-wizard-verify-btn">
                        Verify <ArrowRight size={14} className="ml-1" />
                      </Button>
                      <Button type="button" variant="outline" onClick={onCancel} data-testid="mfa-wizard-cancel-btn">
                        Back to sign in
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {step === STEP_CODES && (
          <Card data-testid="mfa-wizard-codes-step">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-emerald-600" weight="duotone" />
                Step 2 of 2 — Save your recovery codes
              </CardTitle>
              <CardDescription>
                These 10 single-use codes are the ONLY way to sign in if you lose your
                authenticator. They are shown once and can never be viewed again.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2" data-testid="mfa-wizard-recovery-codes">
                {recoveryCodes.map((c) => (
                  <code key={c} className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-sm font-mono text-center">
                    {c}
                  </code>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={copyCodes} data-testid="mfa-wizard-copy-codes-btn">
                  <Copy size={14} className="mr-1" /> Copy all
                </Button>
                <Button variant="outline" size="sm" onClick={downloadCodes} data-testid="mfa-wizard-download-codes-btn">
                  <DownloadSimple size={14} className="mr-1" /> Download .txt
                </Button>
              </div>
              <label className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 cursor-pointer">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(v === true)}
                  className="mt-0.5"
                  data-testid="mfa-wizard-ack-checkbox"
                />
                <span className="text-sm text-amber-900">
                  I have saved these recovery codes in a safe place. I understand they will
                  not be shown again and each code works exactly once.
                </span>
              </label>
              <Button
                className="w-full bg-indigo-900 hover:bg-indigo-800 h-11"
                disabled={!acknowledged || loading}
                onClick={handleAcknowledge}
                data-testid="mfa-wizard-complete-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Continue to AZAI Billing'
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MfaEnrollmentWizard;
